import cx from 'classnames';
import {type FC, memo, type ReactNode, useEffect} from 'react';
import {EnvelopeDisplay} from '~/common/FabricInvitation/EventCreateDesign/components/EnvelopeDisplay';
import {useEnvelopeContext} from '~/common/FabricInvitation/EventCreateDesign/contexts/EnvelopeContext';
import {useOrientation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useOrientation';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {largishAndUp} from '~libSass/base/_exports.sass';

import './CardEnvelopeTemplate.sass';

export enum CardEnvelopeTemplatePosition {
  BEHIND_CARD = 'behind-card',
  IN_FRONT_OF_CARD = 'in-front-of-card',
}

type TemplateEnvelopeProps = {
  position: string;
  // biome-ignore lint/suspicious/noExplicitAny: fix any
  envelopeProps: any;
  orientation: string;
};

const TemplateEnvelope: FC<TemplateEnvelopeProps> = ({position, envelopeProps, orientation}) => {
  const className = cx(
    `card-envelope-template__envelope card-envelope-template__envelope--${orientation}`,
    {
      'card-envelope-template__envelope--behind-card':
        position === CardEnvelopeTemplatePosition.BEHIND_CARD,
      'card-envelope-template__envelope--in-front-of-card':
        position === CardEnvelopeTemplatePosition.IN_FRONT_OF_CARD,
    }
  );

  const mergedProps = {
    type: 'blank',
    className,
    ...envelopeProps,
  };

  return <EnvelopeDisplay {...mergedProps} />;
};

interface CardEnvelopeTemplateProps {
  children: ReactNode;
  hideEnvelope: boolean;
}

export const CardEnvelopeTemplate: FC<CardEnvelopeTemplateProps> = memo(
  ({children, hideEnvelope}) => {
    const {envelopeProps, position, setPosition} = useEnvelopeContext();
    const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
    const orientation = useOrientation();

    // biome-ignore lint/correctness/useExhaustiveDependencies: we want this trigger whenever hideEnvelop changes
    useEffect(() => {
      setPosition(CardEnvelopeTemplatePosition.BEHIND_CARD);
    }, [setPosition, hideEnvelope]);

    return (
      <div
        className={cx('card-envelope-template', {
          [`card-envelope-template--${envelopeProps.type || 'blank'}`]: !isDesktop,
        })}
      >
        {children}
        {hideEnvelope && (
          <TemplateEnvelope
            position={position}
            envelopeProps={envelopeProps}
            orientation={orientation}
          />
        )}
      </div>
    );
  }
);
